import { safeGetItem } from '../../lib/safeStorage';

// Shared by every tool that stages an image into the BackgroundColorTool
// wizard before it's committed to the canvas — a direct file Upload, or an
// already-hosted catalog asset (Clipart/Artwork/Shape) imported via
// POST /api/uploads/import (see importCatalogAssetAsUpload below). Lifted
// out of UploadTool.tsx (which originally only needed to describe its own
// state) once ClipartTool/ArtworkTool/ShapesTool started staging too, per
// direct user request that clipart/artwork/shapes go through the same
// step-by-step wizard a direct upload does instead of dropping straight onto
// the canvas.
export interface StagedImage {
  uploadId: string;
  originalUrl: string;   // pristine upload — never mutated, used by Reset to original
  currentUrl: string;    // what the preview currently shows (result of the latest applied step)
  // Pixel dimensions + embedded DPI of the ORIGINAL upload (all undefined
  // for SVG, which has no fixed pixel resolution/density) — see uploads.ts's
  // response shape. `originalDensity` is further undefined whenever the
  // source file itself carries no resolution tag.
  originalWidth?: number;
  originalHeight?: number;
  originalDensity?: number;
}

// Imports an already-hosted catalog asset (clipart/artwork/shape's
// `image_path`/`shapeMainImage` URL, always same-origin — see the backend
// route's own SSRF guard) as a fresh UploadedImage, so it can be staged into
// the SAME wizard a direct file upload goes through. Returns null (rather
// than throwing) on failure so callers can show a toast instead of an
// unhandled rejection — importing a catalog asset has no user-facing retry
// affordance of its own like a re-pick file dialog does.
export async function importCatalogAssetAsUpload(imageUrl: string): Promise<StagedImage | null> {
  const token = safeGetItem('session', 'customedge_token');
  const res = await fetch('/api/uploads/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ url: imageUrl }),
  });
  if (!res.ok) return null;
  const { url, id, width, height, density } = await res.json();
  return { uploadId: id, originalUrl: url, currentUrl: url, originalWidth: width, originalHeight: height, originalDensity: density };
}
